import { useState } from 'react';
import { flexRender, type Row } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DataTableMobileCardProps<TData> {
  row: Row<TData>;
  onRowClick?: (row: TData) => void;
}

const ACTION_COLUMN_IDS = new Set(['action', 'actions', 'select']);

export function DataTableMobileCard<TData>({ row, onRowClick }: DataTableMobileCardProps<TData>) {
  const [isExpanded, setIsExpanded] = useState(false);
  const cells = row.getVisibleCells();

  const primaryCells = cells.filter((c) => c.column.columnDef.meta?.isPrimary);
  const actionCells = cells.filter((c) => ACTION_COLUMN_IDS.has(c.column.id));

  const fallbackPrimary =
    primaryCells.length === 0 && cells.length > 0
      ? cells.filter((c) => !ACTION_COLUMN_IDS.has(c.column.id)).slice(0, 1)
      : [];

  const visiblePrimary = primaryCells.length > 0 ? primaryCells : fallbackPrimary;
  const primaryIds = new Set(visiblePrimary.map((c) => c.column.id));

  const secondaryCells = cells.filter(
    (c) => !primaryIds.has(c.column.id) && !ACTION_COLUMN_IDS.has(c.column.id),
  );

  const hasDetails = secondaryCells.length > 0 || actionCells.length > 0;

  return (
    <div data-testid="mobile-card" className="bg-card border border-border/40 rounded-xl shadow-sm">
      {/* Header: primary content navigates; chevron is the sole expand/collapse toggle */}
      <div className="flex items-center gap-2 p-4">
        {visiblePrimary.length > 0 && (
          <div
            className="text-sm font-semibold text-foreground min-w-0 flex-1 cursor-pointer active:opacity-70"
            onClick={() => onRowClick?.(row.original)}
          >
            {visiblePrimary.map((cell) => (
              <div key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
            ))}
          </div>
        )}
        {hasDetails && (
          <button
            aria-label="toggle details"
            className="shrink-0 p-1 -mr-1 rounded-md touch-manipulation text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((v) => !v);
            }}
          >
            <ChevronDown
              className={cn(
                'h-4 w-4 transition-transform duration-200',
                isExpanded && 'rotate-180',
              )}
            />
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {secondaryCells.length > 0 && (
            <div className="pt-3 border-t border-border/30 flex flex-col gap-2">
              {secondaryCells.map((cell) => {
                const label = cell.column.columnDef.meta?.label;
                return (
                  <div key={cell.id} className="flex items-baseline gap-2 min-w-0">
                    {label && (
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-medium shrink-0">
                        {label}
                      </span>
                    )}
                    <div className="text-xs text-muted-foreground truncate *:!pl-0 *:!pr-0 *:!text-left">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {actionCells.length > 0 && (
            <div className="pt-3 border-t border-border/30 flex gap-2">
              {actionCells.map((cell) => (
                <div key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
